import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { BalanceCard, Collapsible, IllusCredit_1, theme } from '@sg/garnish';
import type { Credit } from '@sg/graphql-schema';

import { CreditsList } from '../components';
import { useViewDetailsTelemetry } from './CreditOverview.telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditOverviewBalance = (props: CreditOverviewBalanceProps) => {
  const {
    creditAmount,
    creditDetails,
    isLoadingCredits,
    isCollapsible = true,
  } = props;

  const { formatMessage, formatNumber } = useIntl();

  return (
    <View>
      <View style={styles.cardContainer}>
        <BalanceCard
          title={formatMessage(messages.accountCreditLabel)}
          balance={formatNumber(creditAmount / 100, {
            style: 'currency',
            currency: 'USD',
          })}
          illustrationSource={IllusCredit_1}
          isLoading={isLoadingCredits === true}
        />
      </View>

      {}
      {creditAmount > 0 ? (
        isCollapsible ? (
          <CreditOverviewBalanceCollapsible creditDetails={creditDetails} />
        ) : (
          <CreditOverviewBalanceStatic creditDetails={creditDetails} />
        )
      ) : null}
    </View>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const CreditOverviewBalanceCollapsible = (
  props: Pick<CreditOverviewBalanceProps, 'creditDetails'>,
) => {
  const { creditDetails } = props;

  const [isExpanded, setIsExpanded] = React.useState(false);
  const { formatMessage } = useIntl();

  useViewDetailsTelemetry(isExpanded);

  return (
    <Collapsible
      style={styles.fullWidth}
      options={{ hideIcon: true }}
      onChange={setIsExpanded}
    >
      <Collapsible.Details>
        <CreditsList credits={creditDetails} />
      </Collapsible.Details>

      {isExpanded ? (
        <Collapsible.Button
          containerProps={styles.centerSelf}
          style={styles.closeDetails}
          width={136}
          testID="account.available-credit-close-details-button"
          palette="secondary"
          accessibilityRole="button"
          accessibilityLabel={formatMessage(messages.accountCreditCloseDetails)}
          accessibilityHint={formatMessage(messages.accountCreditCloseDetails)}
        >
          {formatMessage(messages.accountCreditCloseDetails)}
        </Collapsible.Button>
      ) : (
        <Collapsible.Button
          containerProps={styles.centerSelf}
          width={136}
          testID="account.available-credit-view-details-button"
          palette="secondary"
          accessibilityRole="button"
          accessibilityLabel={formatMessage(messages.accountCreditViewDetails)}
          accessibilityHint={formatMessage(messages.accountCreditViewDetails)}
        >
          {formatMessage(messages.accountCreditViewDetails)}
        </Collapsible.Button>
      )}
    </Collapsible>
  );
};

const CreditOverviewBalanceStatic = (
  props: Pick<CreditOverviewBalanceProps, 'creditDetails'>,
) => {
  const { creditDetails } = props;

  return (
    <View style={styles.fullWidth}>
      <CreditsList credits={creditDetails} />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'center',

    marginBottom: theme.spacing['8'],
  },
  centerSelf: {
    alignSelf: 'center',
  },
  fullWidth: {
    width: '100%',
  },
  closeDetails: {
    marginTop: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CreditOverviewBalanceProps = Readonly<{
  creditAmount: number;
  creditDetails: readonly Credit[];
  isLoadingCredits?: boolean;
  isCollapsible?: boolean;
}>;

const messages = defineMessages({
  accountCreditLabel: {
    defaultMessage: 'Total credit',
    description: 'Account | Credit | Label',
  },
  accountCreditCloseDetails: {
    defaultMessage: 'Close details',
    description: 'Account | Credit | Close Details',
  },
  accountCreditViewDetails: {
    defaultMessage: 'View details',
    description: 'Account | Credit | View Details',
  },
});
